import { FormInput, FormSelect } from "@common/components/forms";
import { DisplayGroup } from '@common/components/forms/fields';
import { useApp } from "@common/hooks";
import { VisitSalesItemAttributeListValueModel } from "@common/models";
import { IRabiesInformationModel } from '@common/models/RabiesInformationModel';
import { ISalesItemAttributeListValueStorageModel } from "@common/models/requests";
import { observer } from 'mobx-react';
import { Col, Row } from "react-bootstrap";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface IProps {
    attributeListValueModel: ISalesItemAttributeListValueStorageModel;
    setAttributeListValueModel: (value: ISalesItemAttributeListValueStorageModel) => void;
    existingAttributeListValues: VisitSalesItemAttributeListValueModel[];
    setInvalidFormat: (value: boolean) => void;
    salesItemId: number;
    onNewLotNumberChange: (isReadOnly: boolean) => void;
}

export const ExamFormVisitBuilderAddNewLotNumber = observer(({
    attributeListValueModel,
    setAttributeListValueModel,
    existingAttributeListValues,
    setInvalidFormat,
    salesItemId,
    onNewLotNumberChange
}: IProps) => {
    const [showEdit, setShowEdit] = useState<boolean>(false);
    const { configuration } = useApp();
    const { rabiesInformations } = configuration;

    const filteredRabies = useMemo(() => {
        const contents = rabiesInformations.flatMap(r => r.contents);
        const filteredContents = contents.filter(r => r.salesItemId === salesItemId).map(r => r.rabiesInformationId);
        return rabiesInformations.filter(r => filteredContents.includes(r.rabiesInformationId));
    }, [rabiesInformations, salesItemId]);

    const [selectedVaccine, setSelectedVaccine] = useState<IRabiesInformationModel>();
    const { control, resetField } = useFormContext<IRabiesInformationModel>();

    const addNewLotNumber = useWatch({ control, name: 'addNewLotNumber' }) ?? false;
    const newLotNumber = useCallback(() => {
        onNewLotNumberChange(showEdit);

        setShowEdit(!showEdit);
        setSelectedVaccine(undefined);

        resetField('lotNumber');
        resetField('serialNumber');
        resetField('expirationDate');

        const newModel: ISalesItemAttributeListValueStorageModel = {
            ...attributeListValueModel,
            serialNumber: undefined,
            expirationDate: undefined,
            vaccineName: undefined,
            virusType: undefined,
            manufacturer: undefined,
            administered: undefined,
            editing: !showEdit,
        };
        setAttributeListValueModel(newModel);
    }, [showEdit, resetField, attributeListValueModel, onNewLotNumberChange, setAttributeListValueModel]);

    useEffect(() => {
        if (addNewLotNumber === showEdit)
            return;

        newLotNumber();
    }, [newLotNumber, addNewLotNumber, showEdit]);

    const lotNumber = useWatch({ control, name: 'lotNumber' }) ?? "";
    const serialNumber = useWatch({ control, name: 'serialNumber' }) ?? "";

    useEffect(() => {
        const serial = `${lotNumber} / ${serialNumber}`;
        if (lotNumber === "" || serialNumber === "" || serial === attributeListValueModel?.serialNumber) return;

        const newModel: ISalesItemAttributeListValueStorageModel = {
            ...attributeListValueModel,
            serialNumber: serial
        };

        setAttributeListValueModel(newModel);
    }, [serialNumber, lotNumber, attributeListValueModel, setAttributeListValueModel]);


    const expirationDate = useWatch({ control, name: 'expirationDate' }) ?? "";

    useEffect(() => {
        if ((expirationDate === attributeListValueModel.expirationDate) ||
            (expirationDate === "" && attributeListValueModel.expirationDate === undefined)) return;
        let newModel: ISalesItemAttributeListValueStorageModel;

        if (expirationDate === "") {
            newModel = {
                ...attributeListValueModel,
                expirationDate: undefined
            };
        }
        else {
            newModel = {
                ...attributeListValueModel,
                expirationDate: expirationDate
            };
        }
        setAttributeListValueModel(newModel);
    }, [expirationDate, attributeListValueModel, setAttributeListValueModel]);

    const lotNumberInvalid = useCallback(() => {
        return (lotNumber.length < 6 || (lotNumber?.indexOf(" ") >= 1));
    }, [lotNumber]);

    const serialNumberInvalid = useCallback(() => {
        return (serialNumber.length < 6 || (serialNumber?.indexOf(" ") >= 1));
    }, [serialNumber]);

    const lotSerialExists = useCallback(() => {
        const value = attributeListValueModel?.serialNumber;
        if (value === undefined) return false;
        return existingAttributeListValues?.find(a => a.storageParsed.serialNumber.toLowerCase() === value.toLowerCase()) !== undefined;
    }, [attributeListValueModel?.serialNumber, existingAttributeListValues]);

    useEffect(() => {
        setInvalidFormat(lotNumberInvalid() || serialNumberInvalid() || lotSerialExists());
    }, [lotNumberInvalid, serialNumberInvalid, lotSerialExists, setInvalidFormat]);

    return (
        <>
            <FormInput
                label={"Add New Lot # / Serial #"}
                control={control}
                fieldName="addNewLotNumber"
                type="checkbox"
                className="form-check-input"
            />
            {
                showEdit &&
                <>
                    <FormSelect
                        className="mb-2"
                        options={filteredRabies}
                        getOptionLabel={r => r.fullName}
                        getOptionValue={r => r.fullName}
                        onOptionChanged={opt => {
                            if (opt !== null) {
                                setSelectedVaccine(opt);
                                const newModel: ISalesItemAttributeListValueStorageModel = {
                                    ...attributeListValueModel,
                                    administered: opt.vaccineAdministeredName,
                                    vaccineName: opt.vaccineName,
                                    virusType: opt.vaccineVirusTypeName,
                                    manufacturer: opt.vaccineManufacturerName
                                };
                                setAttributeListValueModel(newModel);
                            } else {
                                setSelectedVaccine(undefined);
                                const newModel: ISalesItemAttributeListValueStorageModel = {
                                    ...attributeListValueModel,
                                    administered: '',
                                    vaccineName: '',
                                    virusType: '',
                                    manufacturer: ''
                                };
                                setAttributeListValueModel(newModel);
                            }
                        }}
                        mapOptionToValue={(r) => r?.rabiesInformationId}
                        mapValueToOption={() => _.find(filteredRabies, { rabiesInformationId: selectedVaccine?.rabiesInformationId })}
                        isClearable={true}
                        fieldName={"rabiesInformationId"}
                        control={control}
                        label="Vaccine Name" />
                    <Row>
                        <Col>
                            <FormInput
                                type="text"
                                control={control}
                                label="Lot #"
                                fieldName="lotNumber"
                                className="form-control"
                            />
                            {lotNumberInvalid() && (
                                <div className="text-danger">
                                    Invalid Format Detected. Lot number should be at least 6 characters long and have no whitespace.
                                </div>
                            )}
                        </Col>
                        <Col md={1} className="justify-content-center text-center mt-4">
                            <label className="form-label fs-5">/</label>
                        </Col>
                        <Col>
                            <FormInput
                                type="text"
                                control={control}
                                label="Serial #"
                                fieldName="serialNumber"
                                className="form-control" />
                            {serialNumberInvalid() && (
                                <div className="text-danger">
                                    Invalid Format Detected. Serial number should be at least 6 characters long and have no whitespace.
                                </div>
                            )}
                        </Col>
                    </Row>
                    {lotSerialExists() && (
                        <div className="text-danger">
                            Lot number / Serial number already exists.
                        </div>
                    )}
                    <FormInput
                        label="Exp. Date"
                        type="date"
                        className="form-control"
                        control={control}
                        fieldName="expirationDate"
                        minDate={new Date()}
                        required={true}
                    />
                    <DisplayGroup controlid={"vaccineName"} text={"Vaccine Name"} value={selectedVaccine?.vaccineName} />
                    <DisplayGroup controlid={"vaccineManufacturer"} text={"Vaccine Manufacturer"} value={selectedVaccine?.vaccineManufacturerName} />
                    <DisplayGroup controlid={"vaccineVirusType"} text={"Vaccine Virus Type"} value={selectedVaccine?.vaccineVirusTypeName} />
                    <DisplayGroup controlid={"vaccineAdministered"} text={"Vaccine Administered"} value={selectedVaccine?.vaccineAdministeredName} />
                </>
            }
        </>
    );
});
